import { calculateCustomMetrics } from './calculateCustomMetrics';
import { summarizeChoiceAnswers } from './choiceSummary';
import { getMetricErrors } from './metricErrors';
import { parseReportDefinitionMetrics } from './metricValidation';
import type { MetricQuestion, MetricResponse } from './types';

/** Stored value of one identified metric; `value` is null when no respondent contributed to it. */
export interface IdentifiedMetricValue {
  value: number | null;
  count: number;
}

/**
 * Values of every metric with an `identifier` for the whole report: all `responses` form both the cohort and the
 * population, no viewer filters apply. Metrics without an identifier are calculated only as dependencies.
 * Throws when the definition is invalid or an identified metric (or one it depends on) does not fit the questions.
 */
export function calculateIdentifiedMetrics(
  definition: unknown,
  questions: readonly MetricQuestion[],
  responses: readonly MetricResponse[],
): Record<string, IdentifiedMetricValue> {
  const metrics = parseReportDefinitionMetrics(definition);
  const identified = metrics.filter((metric) => metric.identifier);
  const errors = getMetricErrors(metrics, questions);
  const invalid = identified.filter((metric) => errors[metric.id]);
  if (invalid.length) {
    throw new Error(
      `Invalid identified metrics: ${invalid
        .map((metric) => `${metric.identifier} (${errors[metric.id]})`)
        .join(', ')}`,
    );
  }
  const results = calculateCustomMetrics(
    metrics.filter((metric) => !errors[metric.id]),
    summarizeChoiceAnswers(questions, responses),
    responses,
    responses,
  );
  return Object.fromEntries(
    identified.map((metric) => {
      const result = results[metric.id];
      return [metric.identifier!, { value: result.value ?? null, count: result.count }];
    }),
  );
}
